import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  confirmSignIn,
  fetchAuthSession,
  getCurrentUser,
  signIn,
  signOut,
} from "aws-amplify/auth";
import { Hub } from "@aws-amplify/core";
import ConfirmSignInModal from "../components/login/ConfirmSignInModal";
import { useDisclosure } from "@chakra-ui/react";

export const AuthContext = React.createContext({});

const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const fetchUser = async () => {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();

      setSession(session);
      setUser({
        ...currentUser,
        idToken: session?.tokens?.idToken?.payload,
      });
    } catch (error) {
      console.error(error);
      setSession(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const authListener = (data) => {
      switch (data.payload.event) {
        case "signedIn":
          fetchUser();
          break;
        case "signedOut":
          setUser(null);
          setSession(null);
          break;
        default:
          break;
      }
    };
    const hubListenerCancel = Hub.listen("auth", authListener);

    fetchUser();

    return () => hubListenerCancel();
  }, []);

  const onConfirmSignIn = useCallback(
    async (values) =>
      confirmSignIn({
        challengeResponse: values.newPassword,
        options: { authFlowType: "USER_SRP_AUTH" },
      }),
    [],
  );

  const onSignIn = useCallback(
    async (credentials) => {
      try {
        const { isSignedIn, nextStep } = await signIn({
          ...credentials,
          options: { authFlowType: "USER_SRP_AUTH" },
        });
        if (!isSignedIn) {
          if (
            nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
          ) {
            onOpen();
          }
        }
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    [onOpen],
  );

  const onSignOut = useCallback(async () => {
    try {
      await signOut();
    } catch (error) {
      console.error(error);
      throw error;
    }
  }, []);

  const bearer = useMemo(() => session?.tokens?.idToken?.toString() || null, [session]);

  return (
    <AuthContext.Provider
      value={{
        user,
        bearer,
        loading,
        confirmSignIn: onConfirmSignIn,
        signIn: onSignIn,
        signOut: onSignOut,
      }}
    >
      <ConfirmSignInModal isOpen={isOpen} onClose={onClose} />
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
